/**
 * v109.9.12 — Travel Approver Pool config (Master Admin only)
 * -----------------------------------------------------------
 * Lets Master Admin pick up to 4 users who form the "Travel Advance Approver Pool"
 * and set the amount threshold that forces the 4-tier flow.
 */
import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Users, Settings2, X } from "lucide-react";
import UserPickerDialog from "./UserPickerDialog";

export default function TravelApproverPoolDialog({ open, onClose, onSaved }) {
  const [cfg, setCfg] = useState({ pool_user_ids: [], pool_users: [], amount_threshold: 25000 });
  const [threshold, setThreshold] = useState("25000");
  const [selectedIds, setSelectedIds] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: c }, { data: d }] = await Promise.all([
          api.get("/travel-advances/config"),
          api.get("/directory").catch(() => ({ data: [] })),
        ]);
        setCfg(c || {});
        setSelectedIds(c?.pool_user_ids || []);
        setThreshold(String(c?.amount_threshold || 25000));
        setDirectory(Array.isArray(d) ? d : (d?.users || []));
      } catch (e) {
        toast.error(formatError(e.response?.data?.detail));
      }
    })();
  }, [open]);

  const selectedUsers = directory.filter((u) => selectedIds.includes(u.id));

  const save = async () => {
    if (selectedIds.length > 4) return toast.error("Select at most 4 approvers");
    setSaving(true);
    try {
      const { data } = await api.patch("/travel-advances/config", {
        pool_user_ids: selectedIds,
        amount_threshold: parseFloat(threshold) || 0,
      });
      toast.success("Travel Approver Pool updated");
      setCfg(data);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open && !pickerOpen} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-2xl" data-testid="travel-pool-config-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <Settings2 size={16} />
              Travel Advance · Approver Pool
            </DialogTitle>
            <p className="text-[12px] text-slate-500">
              Pick up to 4 users who will act as the approval pool for Travel Advances.
              When &quot;Bypass CRM&quot; is used <b>OR</b> the amount is ≥ the threshold below,
              <b> all 4</b> must approve. Otherwise, the Reporting Manager + any 1 of these
              users can approve.
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Amount threshold for full 4-tier flow (₹)
              </Label>
              <Input
                type="number" min="0" step="1"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className="mt-1.5 max-w-xs"
                data-testid="travel-pool-threshold"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1.5">
                  <Users size={12} /> Approver Pool ({selectedIds.length}/4)
                </Label>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPickerOpen(true)}
                  data-testid="travel-pool-pick"
                >
                  Pick Users
                </Button>
              </div>
              {selectedUsers.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-300 dark:border-slate-700 p-4 text-center text-[12px] text-slate-500">
                  No approvers selected yet. Click &quot;Pick Users&quot; to choose up to 4.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {selectedUsers.map((u, i) => (
                    <div key={u.id} className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 bg-white dark:bg-slate-900">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">#{i + 1}</Badge>
                        <div>
                          <div className="text-[13px] font-semibold">{u.name}</div>
                          <div className="text-[10.5px] text-slate-500">{u.department || "—"} · {u.employee_code || "—"} · {u.role}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedIds((prev) => prev.filter((x) => x !== u.id))}
                        className="text-slate-400 hover:text-rose-600 p-1"
                        data-testid={`travel-pool-remove-${u.id}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="travel-pool-save"
            >
              {saving ? "Saving…" : "Save Configuration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onDone={(ids) => setSelectedIds(ids.slice(0, 4))}
        users={directory}
        initialSelectedIds={selectedIds}
        title="Select up to 4 Approvers"
      />
    </>
  );
}
